/****************************************************
 * Module Name:  nivodaCriteria.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import renderCellExpand from "../common/dataGridCellExpand";
import { Box, Tooltip, Button } from "@mui/material";
import CommonSnackbar from "../common/commonSnackBar";
import { DataGridPro, gridClasses, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import Constants from "../../utility/constants";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import NivodaUtilityProxy from "../../utility/proxy/nivodautility/nivodautilityProxy";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import AddCriteriaModal from "./addCriteriaModal";
import { LoadingButton } from "@mui/lab";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Papa from 'papaparse';
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import NoPermission from "../common/noPermission";
import { BackupTable } from "@mui/icons-material";
import UploadStockModal from "./uploadStockModal";
import ShowCriteriaStatsModal from "./showCriteriaStatsModal";
import { styled } from '@mui/system';
import Utility from "../../utility/utility";
import NivodaUserProxy from "../../utility/proxy/nivodautility/nivodaUserProxy";
import PageTitle from "../common/pageTitle";

const StyledDataGridPro = styled(DataGridPro)({
    '.MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
        display: 'none',
    },
    '.MuiDataGrid-menuIcon': {
        width: '1px',
        fontSize: '1px'
    },
    [`& .${gridClasses.menuIconButton}`]: {
        marginRight: '-2px',
        fontSize: '1px',
        width: '1px',
        height: '1px',
    }
})

const NivodaCriteria = () => {

    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [editOperation, setEditOperation] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [showAddCriteriaModal, setShowAddCriteriaModal] = useState(false);
    const [criteriaList, setCriteriaList] = useState([]);
    const [sourceData, setSourceData] = useState([]);
    const [criteriaListLoader, setCriteriaListLoader] = useState(false);
    const [uploadSuccessSnackbar, setUploadSuccessSnackbar] = useState(false);
    const [criteriaSuccessSnackbar, setCriteriaSuccessSnackbar] = useState(false);
    const [alertSnackbar, setAlertSnackbar] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [access, setAccess] = useState(false);
    const [uploadStockModal, setUploadStockModal] = useState(false);
    const [showUploadError, setShowUploadError] = useState(false);
    const [criteriaOperation, setCriteriaOperation] = useState('create');
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [userOptions, setUserOptions] = useState([]);

    useEffect(() => {
        PageTitle("ANALYSIS_NIVODA_CRITERIA");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.ANALYSIS_NIVODA_CRITERIA
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        }
        else {
            getAllUser();
        }
    }

    const getAllUser = () => {
        NivodaUserProxy.GetAllDistinctNivodaUser((response) => {
            if (response) {
                setAllUserData(response)
            } else {
                setErrorSnackbar(true)
            }
        }
        );
    };

    const setAllUserData = (response) => {
        let optionsArray = [];
        response.map((username) => {
            let option = { value: username, label: username };
            optionsArray.push(option);
            return true;
        });
        setUserOptions(optionsArray);
    };

    const fetchCriteriaData = (bAnimateSpinner) => {
        bAnimateSpinner && setCriteriaListLoader(true);
        NivodaUtilityProxy.GetAllCriteria(
            (responseData) => {
                const uniqueFilterValues = {
                    certificate: Utility.getDistinctOptions(responseData, "certificate"),
                    carat: Utility.getDistinctOptions(responseData, "carat"),
                    shapes: Utility.getDistinctOptions(responseData, "shapes"),
                    clarity: Utility.getDistinctOptions(responseData, "clarity"),
                    color: Utility.getDistinctOptions(responseData, "color"),
                    cut: Utility.getDistinctOptions(responseData, "cut"),
                    pol: Utility.getDistinctOptions(responseData, "pol"),
                    sym: Utility.getDistinctOptions(responseData, "sym"),
                    flo: Utility.getDistinctOptions(responseData, "flo"),
                    milky: Utility.getDistinctOptions(responseData, "milky"),
                    tinge: Utility.getDistinctOptions(responseData, "tinge"),
                    luster: Utility.getDistinctOptions(responseData, "luster"),
                    user: Utility.getDistinctOptions(responseData, "user"),
                    treatmenttype: Utility.getDistinctOptions(responseData, "treatmenttype"),
                };
                setFilterValues(uniqueFilterValues);
                if (responseData) {
                    setCriteriaList(responseData);
                    setCriteriaListLoader(false);
                } else {
                    setErrorSnackbar(true);
                    setCriteriaListLoader(false);
                }
            }
        );
    };

    const addOrUpdateCriteria = (criteriaData) => {
        setLoadingButton(true);
        setCriteriaOperation(editOperation ? 'update' : 'create');
        if (editOperation) {
            NivodaUtilityProxy.UpdateCriteria(criteriaData, (response) => {
                if (response) {
                    fetchCriteriaData(true);
                    setLoadingButton(false);
                    setShowAddCriteriaModal(false);
                    setCriteriaSuccessSnackbar(true);
                } else {
                    setErrorSnackbar(true);
                    setLoadingButton(false);
                }
            });
        } else {
            NivodaUtilityProxy.AddCriteria(criteriaData, (response) => {
                if (response) {
                    fetchCriteriaData(true);
                    setLoadingButton(false);
                    setShowAddCriteriaModal(false);
                    setCriteriaSuccessSnackbar(true);
                } else {
                    setErrorSnackbar(true);
                    setLoadingButton(false);
                }
            });
        }
    };

    const CreateAllCriteria = (criteriaList) => {
        NivodaUtilityProxy.CreateAllCriteria(criteriaList, (response) => {
            if (response) {
                fetchCriteriaData(true);
                setUploadStockModal(false);
                setLoadingButton(false);
                setUploadLoader(false);
                setUploadSuccessSnackbar(true);
            }
            else {
                setErrorSnackbar(true);
                setUploadStockModal(false);
                setLoadingButton(false);
                setUploadLoader(false);
            }
        })
    }


    const handleValidation = (criteriaData) => {
        //TODO do validation if needed here
        return true;
    };


    const handleClose = () => {
        setAlertSnackbar(false);
        setUploadSuccessSnackbar(false);
        setCriteriaSuccessSnackbar(false);
    };

    const getPriority = (stonetype, excelType) => {
        if (stonetype === 'Natural Diamonds') {
            return excelType === 'Regular' ? 4 : 6;
        } else {
            return excelType === 'Regular' ? 5 : 7;
        }
    }

    const handleUpload = (file, stonetype, filtertype, days, excelType, user, treatmenttype) => {
        const reader = new FileReader();
        setUploadLoader(true);
        setLoadingButton(true);

        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['CARAT', 'SHAPE', 'COLOR', 'CLARITY', 'CUT', 'POL', 'SYM', 'FLR', 'LAB', 'NO', 'MILKY', 'TINGE'];

                    let hasRequiredColumns = requiredColumns.every(col => headersFromCSV.includes(col));
                    if (hasRequiredColumns) {
                        let criteriaList = result.data.map(csvRow => ({
                            id: null,
                            stonetype: stonetype,
                            mystock: 0,
                            color: csvRow['COLOR'],
                            clarity: csvRow['CLARITY'],
                            certificate: csvRow['LAB'],
                            shapes: csvRow['SHAPE'],
                            cut: csvRow['CUT'],
                            sym: csvRow['SYM'],
                            flo: csvRow['FLR'],
                            pol: csvRow['POL'],
                            carat: csvRow['CARAT'],
                            stoneid: csvRow['NO'],
                            status: 'NEW',
                            enable: true,
                            iteration: null,
                            reportid: csvRow['REPORT'],
                            measurements: csvRow['MEASUREMENT'],
                            inscri: csvRow['INSCRI'],
                            location: csvRow['LOCATION'],
                            discount: parseFloat(csvRow['DISC']),
                            rapnet: csvRow['RAP'],
                            depth: parseFloat(csvRow['DEPTH']),
                            table: parseFloat(csvRow['TABLE']),
                            crang: parseFloat(csvRow['CR.ANG.']),
                            pct: parseFloat(csvRow['P/CT']),
                            total: parseFloat(csvRow['TOTAL']),
                            webstatus: csvRow['WEBSTATUS'],
                            days: parseInt(csvRow['DAYS']),
                            request: parseInt(csvRow['REQUEST']),
                            filtertype: filtertype,
                            every: days,
                            milky: csvRow['MILKY'],
                            tinge: csvRow['TINGE'],
                            priority: getPriority(stonetype, excelType),
                            user: user,
                            treatmenttype: treatmenttype
                        }));
                        CreateAllCriteria(criteriaList);
                    } else {
                        console.error("CSV does not contain all required columns.");
                        setUploadLoader(false);
                        setAlertMessage("CSV does not contain all required columns.");
                        setAlertSnackbar(true);
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setAlertMessage("Error parsing CSV");
                    setAlertSnackbar(true);
                    setUploadLoader(false);
                }
            });
        };

        reader.readAsText(file);
    };

    const columns = [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "stoneid",
            headerName: "STONEID",
            headerAlign: "center",
            align: "center",
            width: 90,
            renderCell: renderCellExpand,
        },
        {
            field: "certificate",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.certificate : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "carat",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.carat : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "shapes",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shapes : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "clarity",
            headerName: "CLR",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "color",
            headerName: "COL",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "milky",
            headerName: "MILKY",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.milky : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "tinge",
            headerName: "TINGE",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.tinge : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "luster",
            headerName: "LUSTER",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.luster : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shade : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "singleSelect",
            valueOptions: ["Natural Diamonds", "Lab Grown Diamonds"],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "treatmenttype",
            headerName: "TREATMENT TYPE",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.treatmenttype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "filtertype",
            headerName: "FILTERTYPE",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "singleSelect",
            valueOptions: ["Similar or Better", "Exact"],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "user",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.user : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "mystock",
            headerName: "MYSTOCK",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "boolean",
            renderCell: (params) => <Box>{params.value ? "True" : "False"}</Box>,
        },
        {
            field: "enable",
            headerName: "ENABLE",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "boolean",
            renderCell: (params) => <Box>{params.value ? "True" : "False"}</Box>,
        },
        {
            field: "every",
            headerName: "EVERY (Days)",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => <Box>{params.value === 0 ? "0" : params.value}</Box>
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const rowData = { ...params.row };
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setEditOperation(true)
                            setShowAddCriteriaModal(true)
                            setSourceData(rowData)
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        },
    ];

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                {criteriaListLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            color="primary"
                            fontSize="medium"
                            onClick={() => fetchCriteriaData(true)}
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <Tooltip title="Download Sample Criteria Upload">
                    <a
                        href="/demo_excel/SampleCriteriaUpload.csv"
                        download="SampleCriteriaUpload.csv"
                        style={{ color: "red", marginLeft: "10px" }}
                    >
                        <BackupTable size={25} />
                    </a>
                </Tooltip>
                <Tooltip title="Add Criteria">
                    <Button
                        color="info"
                        variant="outlined"
                        size="small"
                        style={{ marginRight: "10px", marginLeft: "10px" }}
                        onClick={() => {
                            setEditOperation(false)
                            setShowAddCriteriaModal(true)
                            setSourceData([])
                        }
                        }
                        startIcon={<AddBoxOutlinedIcon size="large" />}
                    >
                        Criteria
                    </Button>
                </Tooltip>
                <Tooltip title="Upload Criteria">
                    <LoadingButton
                        color="info"
                        variant="outlined"
                        size="small"
                        sx={{ mr: "10px" }}
                        component="label"
                        loading={uploadLoader}
                        loadingPosition="start"
                        onClick={() => setUploadStockModal(true)}
                        startIcon={<UploadFileIcon size="large" />}
                    >
                        Upload stock
                    </LoadingButton>
                </Tooltip>
                {uploadStockModal && (
                    <UploadStockModal
                        show={uploadStockModal}
                        setShow={setUploadStockModal}
                        userOptions={userOptions}
                        loading={loadingButton}
                        showUploadError={showUploadError}
                        setShowUploadError={setShowUploadError}
                        handleUpload={handleUpload}
                    />
                )}
                <Button style={{ padding: "4px 16px" }} variant="contained" onClick={() => setShowStatsModal(true)}>
                    Show Stats
                </Button>
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        );
    }
    return (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />

            {uploadSuccessSnackbar ? (
                <TabMenuSnackbar
                    show={uploadSuccessSnackbar}
                    message={`File loaded Successfully`}
                    variant={"success"}
                    autoHide={true}
                    title={"success"}
                    handleClose={handleClose}
                />
            ) : null}

            {criteriaSuccessSnackbar ? (
                <TabMenuSnackbar
                    show={criteriaSuccessSnackbar}
                    message={`Criteria ${criteriaOperation === 'update' ? 'updated' : 'created'} successfully`}
                    variant={"success"}
                    autoHide={true}
                    title={"success"}
                    handleClose={handleClose}
                />
            ) : null}

            <TabMenuSnackbar
                show={alertSnackbar}
                message={alertMessage}
                variant={"error"}
                autoHide={false}
                title={"Error"}
                handleClose={handleClose}
            />

            {showAddCriteriaModal ? (
                <AddCriteriaModal
                    Show={true}
                    handleClose={() => setShowAddCriteriaModal(false)}
                    handleSave={(data) => addOrUpdateCriteria(data)}
                    handleValidation={() => handleValidation()}
                    editOperation={editOperation}
                    userOptions={userOptions}
                    sourceData={sourceData}
                    loading={loadingButton}
                />
            ) : null}

            {showStatsModal ? (
                <ShowCriteriaStatsModal
                    show={showStatsModal}
                    handleClose={() => setShowStatsModal(false)}
                />
            ) : null}

            {
                access ? <NoPermission /> : (
                    <Box px={1}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "85vh",
                            }}
                        >
                            <StyledDataGridPro
                                disableRowSelectionOnClick={true}
                                getRowHeight={() => "auto"}
                                density="compact"
                                loading={criteriaListLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: CustomToolbar }}
                                rows={criteriaList}
                                columns={columns}
                                getRowId={(row) => row.id}
                                rowSpacingType="border"
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )}
        </Box>)

}

export default NivodaCriteria;